import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import Layout from '../layout/Layout';
import { paydlesApi } from '../../services/api';
import {
  PaydlePlayInfo,
  PaydleGuess,
  LetterStatus,
  KeyboardLetterState,
  PaydleStatus,
} from '../../types/paydle';
import { GameBoard } from './GameBoard';
import { Keyboard } from './Keyboard';
import { WinModal } from './WinModal';
import { LoseModal } from './LoseModal';
import { useAuth } from '../../hooks/useAuth';
import { MetaTags } from '../SEO';

export const PlayPaydle: React.FC = () => {
  const { shareId } = useParams<{ shareId: string }>();
  const { currentUser, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  const [paydle, setPaydle] = useState<PaydlePlayInfo | null>(null);
  const [attemptId, setAttemptId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [guesses, setGuesses] = useState<PaydleGuess[]>([]);
  const [currentGuess, setCurrentGuess] = useState('');
  const [letterStates, setLetterStates] = useState<KeyboardLetterState>({});
  const [gameStatus, setGameStatus] = useState<PaydleStatus>(PaydleStatus.IN_PROGRESS);
  const [submitting, setSubmitting] = useState(false);

  const [showWinModal, setShowWinModal] = useState(false);
  const [showLoseModal, setShowLoseModal] = useState(false);
  const [revealedWord, setRevealedWord] = useState<string>('');

  // Redirect to sign-up if not authenticated (after auth finishes loading)
  useEffect(() => {
    if (!authLoading && currentUser === null) {
      navigate('/sign-up');
    }
  }, [currentUser, authLoading, navigate]);

  // Load Paydle info (wait for auth to finish loading)
  useEffect(() => {
    const loadPaydle = async () => {
      // Wait for auth to finish loading
      if (authLoading) {
        return;
      }

      // If not authenticated after loading, don't try to load Paydle
      if (!currentUser) {
        return;
      }

      if (!shareId) {
        setError('No share ID provided');
        setLoading(false);
        return;
      }

      try {
        const response = await paydlesApi.getByShareId(shareId);
        const paydleData = response.data as PaydlePlayInfo;

        if (!paydleData.isActive) {
          setError('This Paydle is no longer active');
          setLoading(false);
          return;
        }

        setPaydle(paydleData);

        // Start a new attempt
        const attemptResponse = await paydlesApi.startAttempt(paydleData.id, {
          shareId,
          userId: currentUser.uid,
          userName: currentUser.displayName || currentUser.email || 'Anonymous',
          ipAddress: null,
        });

        setAttemptId(attemptResponse.data.id);
        setLoading(false);
      } catch (err: any) {
        console.error('Error loading Paydle:', err);
        setError(err.response?.data?.error || 'Failed to load Paydle');
        setLoading(false);
      }
    };

    loadPaydle();
  }, [shareId, currentUser, authLoading]);

  // Update letter states based on guesses
  const updateLetterStates = useCallback((guess: PaydleGuess) => {
    const newStates = { ...letterStates };

    guess.word.split('').forEach((letter, index) => {
      const status = guess.letterStatuses[index];
      const currentStatus = newStates[letter];

      // Only update if the new status is better
      if (
        !currentStatus ||
        (status === LetterStatus.CORRECT) ||
        (status === LetterStatus.PRESENT && currentStatus !== LetterStatus.CORRECT)
      ) {
        newStates[letter] = status;
      }
    });

    setLetterStates(newStates);
  }, [letterStates]);

  // Handle key press
  const handleKeyPress = useCallback(
    async (key: string) => {
      if (gameStatus !== PaydleStatus.IN_PROGRESS || submitting) return;

      if (key === 'ENTER') {
        if (currentGuess.length !== 5) {
          // Show error - word must be 5 letters
          return;
        }

        // Submit guess
        setSubmitting(true);
        try {
          if (!paydle || !attemptId) return;

          const response = await paydlesApi.submitGuess(paydle.id, attemptId, currentGuess);
          const {
            letterStatuses,
            status,
            isWin,
            isGameOver,
            word,
          } = response.data;

          // Store the revealed word if game is lost
          if (isGameOver && !isWin && word) {
            setRevealedWord(word);
          }

          const newGuess: PaydleGuess = {
            word: currentGuess,
            letterStatuses,
            timestamp: new Date(),
          };

          setGuesses(prev => [...prev, newGuess]);
          updateLetterStates(newGuess);
          setCurrentGuess('');
          setGameStatus(status);

          // Show modals after animation
          if (isGameOver) {
            setTimeout(() => {
              if (isWin) {
                setShowWinModal(true);
              } else {
                setShowLoseModal(true);
              }
            }, 2000);
          }
        } catch (err: any) {
          console.error('Error submitting guess:', err);
          setError(err.response?.data?.error || 'Failed to submit guess');
        } finally {
          setSubmitting(false);
        }
      } else if (key === 'BACK') {
        setCurrentGuess(prev => prev.slice(0, -1));
      } else if (currentGuess.length < 5) {
        setCurrentGuess(prev => prev + key);
      }
    },
    [currentGuess, gameStatus, submitting, paydle, attemptId, updateLetterStates]
  );

  // Physical keyboard support
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.ctrlKey || e.metaKey || e.altKey) return;

      if (e.key === 'Enter') {
        handleKeyPress('ENTER');
      } else if (e.key === 'Backspace') {
        handleKeyPress('BACK');
      } else if (/^[a-zA-Z]$/.test(e.key)) {
        handleKeyPress(e.key.toUpperCase());
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyPress]);

  // Show loading while auth initializes
  if (authLoading || loading) {
    return (
      <Layout>
        <MetaTags
          title="Loading Paydle..."
          description="Challenge yourself to guess the 5-letter word in 6 tries or pay up!"
          url={`https://spindom.app/paydle/play/${shareId}`}
          image="https://spindom.app/og-image-paydle.png"
        />
        <div className="flex items-center justify-center bg-gray-50 py-20">
          <div className="text-center">
            <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading Paydle...</p>
          </div>
        </div>
      </Layout>
    );
  }

  if (error || !paydle) {
    return (
      <Layout>
        <MetaTags
          title="Paydle Not Found"
          description="This Paydle could not be found or is no longer active."
          url={`https://spindom.app/paydle/play/${shareId}`}
          image="https://spindom.app/og-image-paydle.png"
        />
        <div className="flex items-center justify-center bg-gray-50 p-4 py-20">
          <div className="bg-red-100 border border-red-400 text-red-700 px-6 py-4 rounded-lg max-w-md">
            <h2 className="text-xl font-bold mb-2">Error</h2>
            <p>{error || 'Paydle not found'}</p>
          </div>
        </div>
      </Layout>
    );
  }

  const remainingAttempts = paydle.maxAttempts - guesses.length;

  return (
    <Layout>
      <MetaTags
        title={`Play Paydle - Guess the word or pay $${paydle?.price.toFixed(2)}!`}
        description={paydle?.description || `Challenge yourself to guess the 5-letter word in 6 tries. If you fail, you pay $${paydle?.price.toFixed(2)}!`}
        url={`https://spindom.app/paydle/play/${shareId}`}
        image="https://spindom.app/og-image-paydle.png"
      />
      <div className="bg-gradient-to-br from-purple-50 to-blue-50 py-8 px-4">
        <div className="max-w-2xl mx-auto">
          {/* Header */}
          <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <h1 className="text-5xl font-extrabold text-gray-800 mb-2">
            <span className="bg-gradient-to-r from-purple-600 to-blue-600 bg-clip-text text-transparent">
              Paydle
            </span>
          </h1>
          <p className="text-gray-600">Guess the word or pay ${paydle.price.toFixed(2)}!</p>
          {paydle.description && (
            <p className="text-sm text-gray-500 mt-2 italic">{paydle.description}</p>
          )}
        </motion.div>

        {/* Game info */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-lg shadow-md p-4 mb-6 flex justify-between items-center"
        >
          <div className="text-center flex-1">
            <p className="text-2xl font-bold text-gray-800">{remainingAttempts}</p>
            <p className="text-sm text-gray-600">Guesses Left</p>
          </div>
          <div className="w-px h-12 bg-gray-300"></div>
          <div className="text-center flex-1">
            <p className="text-2xl font-bold text-red-600">${paydle.price.toFixed(2)}</p>
            <p className="text-sm text-gray-600">If You Lose</p>
          </div>
        </motion.div>

        {/* Game Board */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.3 }}
          className="bg-white rounded-lg shadow-lg mb-6 flex justify-center"
        >
          <GameBoard guesses={guesses} currentGuess={currentGuess} maxAttempts={paydle.maxAttempts} />
        </motion.div>

        {/* Error message */}
        {error && gameStatus === PaydleStatus.IN_PROGRESS && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-red-100 border border-red-400 text-red-700 px-4 py-2 rounded mb-4 text-center"
          >
            {error}
          </motion.div>
        )}

        {/* Keyboard */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
        >
          <Keyboard
            onKeyPress={handleKeyPress}
            letterStates={letterStates}
            disabled={gameStatus !== PaydleStatus.IN_PROGRESS || submitting}
          />
        </motion.div>

        {/* Modals */}
        <WinModal
          isOpen={showWinModal}
          onClose={() => setShowWinModal(false)}
          attempts={guesses.length}
          word={guesses[guesses.length - 1]?.word || ''}
        />

        <LoseModal
          isOpen={showLoseModal}
          onClose={() => setShowLoseModal(false)}
          price={paydle.price}
          word={revealedWord}
          creatorName={paydle.creatorName}
        />
        </div>
      </div>
    </Layout>
  );
};
